'use strict';

define('vb/action/builtin/resetDirtyDataStatusAction',[
  'vb/action/action',
  'vb/private/action/actionHelper',
  'vb/private/constants',
  'vb/private/log',
], (Action, ActionHelper, Constants, Log) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */

  const logger = Log.getLogger('/vb/action/builtin/resetDirtyDataStatusAction');

  /**
   * Re-sets the dirty data state of all tracked variables in the current container's
   * scope and all contained containers and their extensions.
   *
   * @extends Action
   * @category Action
   */
  class ResetDirtyDataStatusAction extends Action {
    /**
     * Constructs a new instance of the ResetDirtyDataStatusAction class.
     *
     * @param {string} id - The unique identifier for the action.
     * @param {string} label - The human-readable label for the action.
     */
    constructor(id, label) {
      super(id, label);
      this.log = logger;
    }

    /**
     * Sets the current available context.
     *
     * @param {Object} context - The context object containing information about the current execution environment.
     */
    setContext(context) {
      this.context = context;
    }

    /**
     * Injects the available contexts for expression evaluations.
     *
     * @param {Object} availableContexts - An object containing the available contexts, such as `$application`,
     * `$page`, etc.
     */
    setAvailableContext(availableContexts) {
      this.availableContexts = availableContexts;
    }

    /**
     * Resets the dirty data state of variables being tracked in the current container's scope and all contained
     * containers including their extensions.
     *
     * @param {Object} parameters - Parameters for the action.
     * @param {Array<string>} [parameters.variables] - Optional list of variable expressions to reset. If not provided,
     * resets all variables in the current container's scope and its extensions.
     * @throws {Error} If any error occurs during the reset process.
     * @returns {Outcome} Always returns a success outcome.
     */
    perform(parameters) {
      let exception;
      let variables = parameters.variables || [];

      // Implicitly treat the single variable as an array
      if (!Array.isArray(variables)) {
        variables = [variables];
      }

      // if no params were passed, reset the dirty data the original way, ie,
      // reset the dirty data state of all variables from the current container's scope
      // and all contained containers and their extensions.
      if (!variables.length) {
        const currentContainer = this.context.container;
        currentContainer.resetAllDirtyData();
        return Action.createSuccessOutcome();
      }

      variables.some((variable) => {
        try {
          this.resetDirtyData(variable);
          return false; // keep calling some()
        } catch (e) {
          // let the parent logger get this, so it actually has the action id
          exception = e;
          return true; // terminate the some()
        }
      });

      // this should just throw the error
      if (exception) {
        throw exception;
      }

      return Action.createSuccessOutcome();
    }

    /**
     * Analyzes the expression to determine the variable and then resets the dirty data of the given variable.
     *
     * @param {string} expr - The expression to analyze.
     * @throws {Error} If the expression is invalid or the variable is not found.
     * @description This method uses the `ActionHelper.analyzeExprAst` function to parse the expression and determine
     * the variable to reset. It then calls the `resetDirtyData` method on the variable to reset its dirty data status.
     */
    resetDirtyData(expr) {
      const stack = ActionHelper.analyzeExprAst(expr, this.availableContexts);
      let scope = null;
      let variableName = null;

      if (stack.length < 1) {
        throw new Error('Invalid empty expression.');
      }

      const scopeName = stack[0].name;
      if (Constants.ALL_SCOPES.indexOf(scopeName) !== -1) {
        if (scopeName !== '$variables') {
          scope = stack[0].value;

          if (stack.length < 2) {
            throw ActionHelper.getMissingNamespaceError(expr);
          }

          // With the introduction of $base, the namespace following up can be page, flow or application
          if (scopeName === '$base') {
            scope = ActionHelper.checkBase(expr, stack);
          } else if (scopeName === '$extension') {
            const baseName = stack[1].name;
            if (baseName !== 'base') {
              throw ActionHelper.getInvalidNamespaceError(baseName, expr);
            }

            // Remove $extension from stack
            stack.splice(0, 1);

            scope = ActionHelper.checkBase(expr, stack);
          }

          if (!scope) {
            throw new Error(
              `${scopeName} is not accessible in expression ${expr} from the current action chain.`,
            );
          }

          const namespace = stack[1].name;

          if (namespace !== Constants.VariableNamespace.VARIABLES) {
            throw new Error(
              `Cannot reset dirty data status, invalid namespace ${namespace} in expression ${expr}.`,
            );
          }

          if (stack.length < 3) {
            throw ActionHelper.getMissingVariableNameError(expr);
          }

          variableName = stack[2].name;
        } else {
          scope = this.context.chainRunner.getTargetContainerScope(this.availableContexts);

          if (stack.length < 2) {
            throw ActionHelper.getMissingVariableNameError(expr);
          }

          variableName = stack[1].name;
        }

        const variable = scope && scope.getVariable(variableName, Constants.VariableNamespace.VARIABLES);
        if (variable) {
          variable.resetDirtyData();
        }
      } else {
        // invalid scope name
        throw new Error(`Invalid scope name ${scopeName} in expression ${expr}.`);
      }
    }
  }

  return ResetDirtyDataStatusAction;
});

